import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Avatar,
  Divider,
  CircularProgress,
  Chip,
  Popover,
} from '@mui/material';
import {
  Send as SendIcon,
  ChatBubbleOutline as CommentIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
} from '@mui/icons-material';
import { aiBuilderAPI } from '../services/api';

interface Comment {
  id: number;
  comment_text: string;
  author: number;
  author_name: string;
  created_at: string;
  updated_at: string;
  time_ago: string;
}

interface CommentSectionProps {
  entityType: 'task' | 'subtask';
  entityId: number;
  onCommentsChange?: () => void;
  alwaysExpanded?: boolean; // For use in full-width rows
  showToggleButton?: boolean; // Show toggle button with count, no popup
  isExpanded?: boolean; // External expanded state
  onToggleExpand?: () => void; // External toggle handler
  initialCommentCount?: number; // Pre-loaded comment count to avoid API call
}

const CommentSection: React.FC<CommentSectionProps> = ({
  entityType,
  entityId,
  onCommentsChange,
  alwaysExpanded = false,
  showToggleButton = false,
  isExpanded = false,
  onToggleExpand,
  initialCommentCount,
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totalCount, setTotalCount] = useState(initialCommentCount ?? 0);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const COMMENTS_PER_PAGE = 3;

  // Update totalCount if initialCommentCount prop changes
  useEffect(() => {
    if (initialCommentCount !== undefined) {
      setTotalCount(initialCommentCount);
    }
  }, [initialCommentCount]);

  // Load comments when expanded or always expanded
  useEffect(() => {
    const shouldLoadComments = alwaysExpanded || (showToggleButton && isExpanded) || internalExpanded;
    if (shouldLoadComments) {
      loadComments(0); // Start from the beginning when expanding
    }
  }, [isExpanded, internalExpanded, alwaysExpanded, showToggleButton, entityId]);

  const loadComments = async (offset: number = 0) => {
    setIsLoading(true);
    try {
      const response = await aiBuilderAPI.getComments(entityType, entityId, offset);
      
      if (offset === 0) {
        // New load - replace comments
        setComments(response.comments);
      } else {
        // Pagination - append comments
        setComments(prev => [...prev, ...response.comments]);
      }
      
      setTotalCount(response.total_count);
      setCurrentOffset(offset);
      setHasMore(response.has_more);
      
      console.log(`✅ Loaded ${response.comments.length} comments for ${entityType} ${entityId}`);
    } catch (error) {
      console.error(`❌ Error loading comments for ${entityType} ${entityId}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    
    setIsSubmitting(true);
    try {
      const createdComment = await aiBuilderAPI.createComment(entityType, entityId, newComment.trim());
      
      // Add the new comment to the top of the list (newest first)
      setComments(prev => [createdComment, ...prev]);
      setNewComment('');
      setTotalCount(prev => prev + 1);
      
      // Call parent callback immediately to trigger re-render of other components
      if (onCommentsChange) {
        onCommentsChange();
      }
      
      console.log(`✅ Created comment for ${entityType} ${entityId}`);
    } catch (error) {
      console.error(`❌ Error creating comment for ${entityType} ${entityId}:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoadMore = () => {
    const nextOffset = currentOffset + COMMENTS_PER_PAGE;
    loadComments(nextOffset);
  };

  const handleLoadPrevious = () => {
    if (currentOffset > 0) {
      const prevOffset = Math.max(0, currentOffset - COMMENTS_PER_PAGE);
      loadComments(prevOffset);
    }
  };

  const toggleExpanded = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (alwaysExpanded) {
      // In always expanded mode, this shouldn't be called
      return;
    }
    
    if (showToggleButton && onToggleExpand) {
      // Use external toggle handler
      onToggleExpand();
    } else {
      // Use internal state and popover
      if (event) {
        setAnchorEl(event.currentTarget);
      }
      
      setInternalExpanded(!internalExpanded);
      if (!internalExpanded) {
        // When expanding, reset to first page
        setCurrentOffset(0);
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setInternalExpanded(false);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Render toggle button mode (just button with count, no popup)
  if (showToggleButton) {
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <IconButton
          size="small"
          onClick={toggleExpanded}
          color={isExpanded ? "primary" : (totalCount > 0 ? "primary" : "default")}
          title={totalCount > 0 ? `${totalCount} comments` : 'No comments'}
        >
          <CommentIcon />
        </IconButton>
        {totalCount > 0 && (
          <Chip
            label={totalCount}
            size="small"
            color="primary"
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              height: 16,
              minWidth: 16,
              fontSize: '0.6rem',
              '& .MuiChip-label': {
                px: 0.5,
              },
            }}
          />
        )}
      </Box>
    );
  }

  // Render comment indicator for popup mode (only if not always expanded)
  if (!internalExpanded && !alwaysExpanded) {
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <IconButton
          size="small"
          onClick={(e) => toggleExpanded(e)}
          color={totalCount > 0 ? "primary" : "default"}
          title={totalCount > 0 ? `${totalCount} comments` : 'No comments'}
        >
          <CommentIcon />
        </IconButton>
        {totalCount > 0 && (
          <Chip
            label={totalCount}
            size="small"
            color="primary"
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              height: 16,
              minWidth: 16,
              fontSize: '0.6rem',
              '& .MuiChip-label': {
                px: 0.5,
              },
            }}
          />
        )}
      </Box>
    );
  }

  // Render popover for inline mode or full section for always expanded
  const commentContent = (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CommentIcon fontSize="small" />
          Comments ({totalCount})
        </Typography>
        {!alwaysExpanded && (
          <IconButton size="small" onClick={handleClose}>
            <UpIcon />
          </IconButton>
        )}
      </Box>

        {/* Comments List */}
        {isLoading && comments.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* Navigation Controls */}
            {totalCount > COMMENTS_PER_PAGE && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Button
                  size="small"
                  startIcon={<UpIcon />}
                  onClick={handleLoadPrevious}
                  disabled={currentOffset === 0 || isLoading}
                >
                  Newer
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Showing {Math.min(currentOffset + COMMENTS_PER_PAGE, totalCount)} of {totalCount}
                </Typography>
                <Button
                  size="small"
                  endIcon={<DownIcon />}
                  onClick={handleLoadMore}
                  disabled={!hasMore || isLoading}
                >
                  Older
                </Button>
              </Box>
            )}

            {/* Comments */}
            {comments.length > 0 ? (
              <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                {comments.map((comment, index) => (
                  <Box key={comment.id}>
                    <Box sx={{ display: 'flex', gap: 1, py: 1 }}>
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: '0.75rem',
                          bgcolor: 'primary.main',
                        }}
                      >
                        {getInitials(comment.author_name)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2" fontSize="0.8rem">
                            {comment.author_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {comment.time_ago}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontSize="0.85rem">
                          {comment.comment_text}
                        </Typography>
                      </Box>
                    </Box>
                    {index < comments.length - 1 && <Divider sx={{ my: 1 }} />}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No comments yet
              </Typography>
            )}

            {/* Add Comment */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitComment();
                  }
                }}
                multiline
                minRows={1}
                maxRows={3}
                inputProps={{ maxLength: 1000 }}
                disabled={isSubmitting}
              />
              <Button
                variant="contained"
                size="small"
                endIcon={isSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
                onClick={handleSubmitComment}
                disabled={!newComment.trim() || isSubmitting}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                {isSubmitting ? '' : 'Send'}
              </Button>
            </Box>
            
            {/* Character count */}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {newComment.length}/1000 characters
            </Typography>
          </>
        )}
    </>
  );

  // Render based on mode
  if (alwaysExpanded) {
    // Full-width row mode
    return (
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ p: 2 }}>
          {commentContent}
        </Paper>
      </Box>
    );
  } else {
    // Popover mode (only used when not showToggleButton)
    return (
      <>
        <Popover
          open={Boolean(anchorEl) && internalExpanded}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Paper sx={{ p: 2, width: 400, maxHeight: 500, overflow: 'auto' }}>
            {commentContent}
          </Paper>
        </Popover>
      </>
    );
  }
};

export default CommentSection;